import { useMutation } from '@tanstack/react-query';
import { toastErrorConfig } from 'lib/reactToastify/config';
import { toast } from 'react-toastify';

import { logError } from 'lib/sentry/logError';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';

import { CHAT_MESSAGE_TYPES } from 'features/chat';
import {
  UUID,
  BaseMessage,
  Message,
  Messages,
} from 'features/chat/config/types/Message';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';
import { useChatMessagesQuery } from 'features/chat/hooks/query/useChatMessages';
import { useChatQueryActions } from 'features/chat/hooks/useChatActions';
import { updateMessage } from 'features/chat/utils/messageUpdaters';

export const useChatMessageDelete = () => {
  const { chatId } = useChatInterlocutorContext();
  const { request } = useMutationHTTPRequest();
  const { cancelChatQueries, getChatQuery, updateChatQuery } =
    useChatQueryActions();

  const { setNewMessages } = useChatMessagesQuery({
    chatId,
  });

  return useMutation(
    async ({ messageId }: { messageId: UUID }) =>
      request({
        url: `/api/chats/${chatId}/messages/${messageId}/delete`,
      }),
    {
      onMutate: async ({ messageId }) => {
        await cancelChatQueries();

        const previousValue = getChatQuery();

        setNewMessages((prev) =>
          updateMessage(prev, (msg) => markMessageAsDeleted(msg, messageId))
        );

        return { previousValue };
      },

      onError: (err, _, context) => {
        const previousValue = context?.previousValue as Messages;

        updateChatQuery(previousValue);

        toast.error(
          "There's an error with deleting the message. Please try again later.",
          toastErrorConfig
        );

        logError(err);
      },
    }
  );
};

const markMessageAsDeleted = (msg: BaseMessage, messageId: string) => {
  const deletedMessageAttributes = {
    text: 'The message was deleted',
    deleted: true,
  };

  if (msg.messageId === messageId) {
    return {
      ...msg,
      type: CHAT_MESSAGE_TYPES.DELETED,
      content: {
        type: CHAT_MESSAGE_TYPES.DELETED,
        text: 'The message was deleted',
      },
      ...deletedMessageAttributes,
    } as Message;
  }

  if (msg.replyToMessage?.messageId === messageId) {
    return {
      ...msg,
      replyToMessage: {
        ...msg.replyToMessage,
        ...deletedMessageAttributes,
      },
    } as Message;
  }

  return null;
};
